import { IComboVariant, IProduct } from '@/app/[locale]/_types'
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
} from '@/components/ui/dialog'
import React from 'react'
import {
  Box,
  Heading,
  IconButton,
  SimpleGrid,
  Stack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import Details from './components/Details'
import Slider from '../../../Slider'
import Description from '../../../Description'
import { useCustomLocale } from '@/app/[locale]/_hooks/useCustomLocale'
import Ingredients from '../../../Ingredients'
import { Alert } from '@/components/ui/alert'
import { BackIcon } from '@/app/[locale]/_assets/icon-components'

function PreviewModal({
  data,
  isOpen,
  onOpenChange,
  onAdd,
}: {
  data: IProduct | undefined
  selectedVariant: IComboVariant
  isOpen: boolean
  onOpenChange: (e: boolean) => void
  onAdd: (e: any) => void
}) {
  const isDesktop = useBreakpointValue(
    { base: false, lg: true },
    { ssr: false }
  )

  if (isDesktop)
    return (
      <Desktop
        data={data}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        onAdd={onAdd}
      />
    )
  return (
    <Mobile
      data={data}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onAdd={onAdd}
    />
  )
}

const Desktop = ({
  data,
  isOpen,
  onOpenChange,
  onAdd,
}: {
  data: IProduct | undefined
  isOpen: boolean
  onOpenChange: (e: boolean) => void
  onAdd: (e: any) => void
}) => {
  const currentLocale = useCustomLocale()

  return (
    <DialogRoot
      open={isOpen}
      size='cover'
      placement='center'
      scrollBehavior='inside'
      motionPreset='slide-in-bottom'
      onOpenChange={(e) => onOpenChange(e.open)}
    >
      <DialogContent maxW={'1200px'} rounded='xl'>
        <DialogHeader p={0} pt={2}></DialogHeader>
        <DialogBody p={0} px={2} color='text'>
          <SimpleGrid
            gridTemplateColumns={{ base: '1fr', lg: '500px 1fr' }}
            gap={10}
            h='full'
          >
            <Slider list={[data?.image && data?.image, ...data?.gallery]} />
            <Stack gap={0} p={6} pb={0} pr={4} position='relative'>
              <Box>
                <Text
                  pb={2}
                  as='h1'
                  fontSize='xl'
                  lineHeight={1.2}
                  fontWeight={600}
                >
                  {data?.title[currentLocale]}
                </Text>
                <Description data={data} />
              </Box>

              <Ingredients data={data} />
              {data?.allergens?.[currentLocale] && (
                <Alert
                  mb={4}
                  rounded={12}
                  status='warning'
                  title={data?.allergens?.[currentLocale]}
                />
              )}
              <Details data={data} onAdd={onAdd} />
            </Stack>
          </SimpleGrid>
        </DialogBody>
        <DialogCloseTrigger
          display={{ base: 'none', lg: 'flex' }}
          color='text'
          _hover={{ bg: 'background' }}
        />
        <DialogFooter pt={0}></DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}

const Mobile = ({
  data,
  isOpen,
  onOpenChange,
  onAdd,
}: {
  data: IProduct | undefined
  isOpen: boolean
  onOpenChange: (e: boolean) => void
  onAdd: (e: any) => void
}) => {
  const currentLocale = useCustomLocale()

  return (
    <DialogRoot
      lazyMount
      size='full'
      open={isOpen}
      placement='center'
      scrollBehavior='inside'
      motionPreset='slide-in-bottom'
      onOpenChange={(e) => onOpenChange(e.open)}
    >
      <DialogContent portalled={false}>
        <IconButton
          zIndex={99}
          top={1}
          left={1}
          onClick={() => onOpenChange(false)}
          position={'absolute'}
          variant={'subtle'}
          rounded={'full'}
          bg='background.card'
          color='text'
        >
          <BackIcon color='currentColor' />
        </IconButton>
        <DialogBody p={0} display='flex' flexDir='column'>
          <Slider list={[data?.image && data?.image, ...data?.gallery]} />
          <Stack position='relative' flex={1} gap={0}>
            <Box px={4}>
              <Text
                py={{ base: 2, lg: 0 }}
                as={'h1'}
                fontSize={'20px'}
                fontWeight={600}
              >
                {data?.title[currentLocale]}
              </Text>
              <Description data={data} />
              {/* <Nutrients data={data} /> */}
            </Box>
            <Details data={data} onAdd={onAdd} />
          </Stack>
        </DialogBody>
        <DialogCloseTrigger display={{ base: 'none', lg: 'flex' }} />
      </DialogContent>
    </DialogRoot>
  )
}

export default PreviewModal
